import React, { useState } from 'react';
import {
    Modal,
    Upload,
    Button,
    Table,
    Typography,
    Space,
    Alert,
    message,
    Tag,
    Divider,
    Result
} from 'antd';
import {
    UploadOutlined,
    DownloadOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import auctionService from '../../services/auctionService';

const { Text, Title, Paragraph } = Typography;

const REQUIRED_HEADERS = ['first_name', 'last_name', 'email', 'password'];

const sampleCsvContent = [
    REQUIRED_HEADERS.join(','),
    'Ali,Khan,ali.example@email.com,TempPass123!',
    'Sara,Ahmed,sara.example@email.com,TempPass456!'
].join('\n');

const BulkUploadUsers = ({ auction, visible, onClose, onSuccess }) => {
    const [csvData, setCsvData] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);

    const resetState = () => {
        setCsvData([]);
        setValidationErrors([]);
        setUploadResults(null);
        setUploading(false);
    };

    const handleClose = () => {
        resetState();
        onClose?.();
    };

    const downloadSampleCsv = () => {
        const blob = new Blob([sampleCsvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'bulk_users_sample.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const parseCsv = (csvText) => {
        const lines = csvText.split(/\r?\n/).filter((line) => line.trim());
        if (lines.length < 2) {
            throw new Error('CSV file must have a header row and at least one data row');
        }

        const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase());
        const missing = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
        if (missing.length > 0) {
            throw new Error(`Missing required columns: ${missing.join(', ')}`);
        }

        const data = [];
        for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
            if (values.every((v) => !v)) continue;

            const row = {};
            headers.forEach((header, index) => {
                row[header] = values[index] || '';
            });
            data.push(row);
        }

        if (data.length === 0) {
            throw new Error('No valid data rows found in CSV file');
        }

        return data;
    };

    const validateData = (data) => {
        const errors = [];
        const emails = new Set();

        data.forEach((row, index) => {
            const rowNumber = index + 2;
            const email = (row.email || '').trim().toLowerCase();

            if (!row.first_name?.trim()) {
                errors.push({ row: rowNumber, message: 'first_name is required' });
            }
            if (!row.last_name?.trim()) {
                errors.push({ row: rowNumber, message: 'last_name is required' });
            }
            if (!email) {
                errors.push({ row: rowNumber, message: 'email is required' });
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                errors.push({ row: rowNumber, message: 'invalid email format' });
            } else if (emails.has(email)) {
                errors.push({ row: rowNumber, message: 'duplicate email in CSV' });
            } else {
                emails.add(email);
            }

            if (!row.password?.trim()) {
                errors.push({ row: rowNumber, message: 'password is required' });
            } else if (row.password.trim().length < 8) {
                errors.push({ row: rowNumber, message: 'password must be at least 8 characters' });
            }
        });

        setValidationErrors(errors);
        return errors.length === 0;
    };

    const handleFileUpload = (info) => {
        const file = info.file?.originFileObj || info.file;
        if (!file) return;

        if (file.type !== 'text/csv' && !file.name?.endsWith('.csv')) {
            message.error('Please upload a CSV file');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = parseCsv(e.target.result);
                setCsvData(data);
                setUploadResults(null);
                validateData(data);
                message.success(`Loaded ${data.length} row(s) from CSV`);
            } catch (error) {
                setCsvData([]);
                setValidationErrors([]);
                message.error(error.message || 'Failed to parse CSV');
            }
        };
        reader.readAsText(file);
    };

    const handleUpload = async () => {
        if (!auction?.slug) return;
        if (!csvData.length) {
            message.warning('Please upload a CSV file first');
            return;
        }
        if (!validateData(csvData)) {
            message.error('Please fix validation errors before uploading');
            return;
        }

        try {
            setUploading(true);
            const payload = csvData.map((row) => ({
                first_name: row.first_name.trim(),
                last_name: row.last_name.trim(),
                email: row.email.trim().toLowerCase(),
                password: row.password.trim()
            }));

            const response = await auctionService.bulkUploadUsers(auction.slug, payload);
            setUploadResults(response.data);
            message.success('Bulk user upload completed');
            onSuccess?.();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to upload users');
        } finally {
            setUploading(false);
        }
    };

    const previewColumns = [
        { title: 'First Name', dataIndex: 'first_name', key: 'first_name' },
        { title: 'Last Name', dataIndex: 'last_name', key: 'last_name' },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        {
            title: 'Temp Password',
            dataIndex: 'password',
            key: 'password',
            render: (value) => <Text code>{value}</Text>
        }
    ];

    const summary = uploadResults?.summary;

    return (
        <Modal
            title={
                <Space>
                    <UploadOutlined />
                    <span>Upload Bulk Users</span>
                </Space>
            }
            open={visible}
            onCancel={handleClose}
            width={900}
            footer={
                uploadResults ? (
                    <Button type="primary" onClick={handleClose}>
                        Done
                    </Button>
                ) : (
                    <Space>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button
                            type="primary"
                            loading={uploading}
                            onClick={handleUpload}
                            disabled={!csvData.length || validationErrors.length > 0}
                        >
                            Upload Users
                        </Button>
                    </Space>
                )
            }
            destroyOnClose
        >
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Private auction: ${auction?.title || ''}`}
                description={
                    <div>
                        <Paragraph style={{ marginBottom: 8 }}>
                            Upload a CSV with approved accounts. New users are created with a temporary password and added as accepted participants. Existing users are only added to this auction (password unchanged).
                        </Paragraph>
                        <Paragraph style={{ marginBottom: 0 }}>
                            Newly created users must change their temporary password on first login before using the app.
                        </Paragraph>
                    </div>
                }
            />

            {!uploadResults && (
                <>
                    <Space style={{ marginBottom: 16 }} wrap>
                        <Button icon={<DownloadOutlined />} onClick={downloadSampleCsv}>
                            Download Sample CSV
                        </Button>
                        <Upload
                            accept=".csv,text/csv"
                            showUploadList={false}
                            beforeUpload={() => false}
                            onChange={handleFileUpload}
                            maxCount={1}
                        >
                            <Button icon={<FileTextOutlined />}>Select CSV File</Button>
                        </Upload>
                    </Space>

                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="CSV format"
                        description="Columns required: first_name, last_name, email, password (temporary password, min 8 characters)."
                    />

                    {validationErrors.length > 0 && (
                        <Alert
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message={`${validationErrors.length} validation error(s)`}
                            description={
                                <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 140, overflow: 'auto' }}>
                                    {validationErrors.slice(0, 20).map((err, idx) => (
                                        <li key={idx}>Row {err.row}: {err.message}</li>
                                    ))}
                                </ul>
                            }
                        />
                    )}

                    {csvData.length > 0 && (
                        <>
                            <Divider />
                            <Title level={5}>Preview ({csvData.length} users)</Title>
                            <Table
                                size="small"
                                rowKey={(_, index) => index}
                                columns={previewColumns}
                                dataSource={csvData}
                                pagination={{ pageSize: 5 }}
                                scroll={{ x: true }}
                            />
                        </>
                    )}
                </>
            )}

            {uploadResults && (
                <>
                    <Result
                        status="success"
                        title="Bulk upload finished"
                        subTitle={
                            <Space wrap>
                                <Tag color="green" icon={<CheckCircleOutlined />}>
                                    Created: {summary?.created || 0}
                                </Tag>
                                <Tag color="blue">
                                    Existing added: {summary?.added_existing || 0}
                                </Tag>
                                <Tag color="orange">
                                    Skipped: {summary?.skipped || 0}
                                </Tag>
                                <Tag color="red" icon={<ExclamationCircleOutlined />}>
                                    Errors: {summary?.errors || 0}
                                </Tag>
                            </Space>
                        }
                    />

                    {(uploadResults.results?.errors?.length > 0 || uploadResults.results?.skipped?.length > 0) && (
                        <Alert
                            type="info"
                            showIcon
                            message="Details"
                            description={
                                <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 180, overflow: 'auto' }}>
                                    {[...(uploadResults.results.errors || []), ...(uploadResults.results.skipped || [])]
                                        .slice(0, 30)
                                        .map((item, idx) => (
                                            <li key={idx}>
                                                Row {item.row} ({item.email}): {item.reason}
                                            </li>
                                        ))}
                                </ul>
                            }
                        />
                    )}
                </>
            )}
        </Modal>
    );
};

export default BulkUploadUsers;
